import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { gsap } from 'gsap'
import styles from './HandwritingView.module.scss'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import languages from '../languages'
import { celebrate, shake } from './juice'
import { romanize as romanizeKorean } from '@romanize/korean'

import CloseIcon from '@mui/icons-material/Close'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import UndoIcon from '@mui/icons-material/Undo'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import CheckIcon from '@mui/icons-material/Check'

// A flashcard has two sides; we practice writing the non-English one.
const practiceSideOf = (card) => {
  const t = card.translations?.[0]
  if (card.sourceLanguage && card.sourceLanguage !== 'eng' && card.source) {
    return { text: card.source, lang: card.sourceLanguage, translation: t?.target || '' }
  }
  if (t?.target && t.targetLanguage !== 'eng') {
    return { text: t.target, lang: t.targetLanguage, translation: card.source || '' }
  }
  return { text: card.source, lang: card.sourceLanguage || 'eng', translation: t?.target || '' }
}

const romanizationOf = (text, lang) => {
  if (lang !== 'kor' || !text) return null
  try { return romanizeKorean(text) } catch { return null }
}

// Pressure-sensitive stroke rendering — same approach as the note dialog's
// drawing layer, but in plain board pixel coordinates.
function StrokeRender({ stroke }) {
  if (!stroke || stroke.points.length === 0) return null
  if (stroke.points.length === 1) {
    const p = stroke.points[0]
    return <circle cx={p.x} cy={p.y} r={(stroke.width * (p.pressure ?? 0.5) + 0.5) / 2} fill={stroke.color} />
  }
  const segs = []
  for (let i = 1; i < stroke.points.length; i++) {
    const p1 = stroke.points[i - 1]
    const p2 = stroke.points[i]
    const avg = ((p1.pressure ?? 0.5) + (p2.pressure ?? 0.5)) / 2
    const w = stroke.width * (0.35 + avg * 1.3) + 0.3
    segs.push(
      <line key={i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
        stroke={stroke.color} strokeWidth={w} strokeLinecap="round" />
    )
  }
  return <g>{segs}</g>
}

const PEN_WIDTH = 6
const INK_COLOR = '#e8e8ec'

export default function HandwritingView({ cards, voiceSpeed, onClose, autoSpeak = false, checkDrawing = false }) {
  const items = useMemo(
    () => (cards || []).map(practiceSideOf).filter(i => i.text && i.text.trim()),
    [cards]
  )

  const [cardIdx, setCardIdx] = useState(0)
  const [charIdx, setCharIdx] = useState(0)
  const [doneChars, setDoneChars] = useState(new Set())
  const [wordComplete, setWordComplete] = useState(false)
  const [showGuide, setShowGuide] = useState(true)
  const [strokes, setStrokes] = useState([])
  const [currentStroke, setCurrentStroke] = useState(null)
  const [drawFeedback, setDrawFeedback] = useState('')

  const boardRef = useRef(null)
  const ghostRef = useRef(null)
  const isDrawingRef = useRef(false)
  const advanceTimerRef = useRef(null)

  const item = items[cardIdx]
  const chars = useMemo(
    () => Array.from(item?.text || '').filter(ch => ch.trim()),
    [item]
  )
  const currentChar = chars[charIdx]

  // Ghost glyph springs in whenever the character changes.
  useEffect(() => {
    setStrokes([])
    setCurrentStroke(null)
    setDrawFeedback('')
    if (ghostRef.current) {
      gsap.fromTo(ghostRef.current,
        { scale: 0.75, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.45, ease: 'back.out(1.8)' }
      )
    }
  }, [cardIdx, charIdx])

  useEffect(() => () => clearTimeout(advanceTimerRef.current), [])

  // "Hear it, then write it" — auto-play the word's sound when it appears.
  useEffect(() => {
    if (!autoSpeak || !item?.text) return
    const t = setTimeout(() => speak(item.text, voices[item.lang] || voices['eng'], voiceSpeed), 300)
    return () => clearTimeout(t)
  }, [cardIdx, autoSpeak]) // eslint-disable-line

  const boardPoint = (e) => {
    const rect = boardRef.current.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top, pressure: e.pressure || 0.5 }
  }

  const onPointerDown = (e) => {
    if (wordComplete) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    isDrawingRef.current = true
    setCurrentStroke({
      id: `s_${Date.now()}_${Math.floor(Math.random() * 1e6)}`,
      color: INK_COLOR,
      width: PEN_WIDTH,
      points: [boardPoint(e)],
    })
  }

  const onPointerMove = (e) => {
    if (!isDrawingRef.current) return
    e.preventDefault()
    const p = boardPoint(e)
    setCurrentStroke(prev => {
      if (!prev) return prev
      const last = prev.points[prev.points.length - 1]
      if (last && Math.abs(last.x - p.x) < 0.5 && Math.abs(last.y - p.y) < 0.5) return prev
      return { ...prev, points: [...prev.points, p] }
    })
  }

  const onPointerUp = () => {
    if (!isDrawingRef.current) return
    isDrawingRef.current = false
    setCurrentStroke(stroke => {
      if (stroke && stroke.points.length > 0) setStrokes(prev => [...prev, stroke])
      return null
    })
  }

  const undo = () => setStrokes(prev => prev.slice(0, -1))
  const clear = () => { setStrokes([]); setCurrentStroke(null) }

  const speakText = useCallback((text) => {
    if (!text || !item) return
    speak(text, voices[item.lang] || voices['eng'], voiceSpeed)
  }, [item, voiceSpeed])

  const nextWord = useCallback(() => {
    setCardIdx(prev => (prev + 1) % Math.max(items.length, 1))
    setCharIdx(0)
    setDoneChars(new Set())
    setWordComplete(false)
    setStrokes([])
  }, [items.length])

  const markCharDone = () => {
    if (wordComplete) return
    const rect = boardRef.current?.getBoundingClientRect()

    // Light validation: did they actually write a full-sized character inside the
    // box ("between the lines")? Blocks only blank/tiny scribbles; it isn't shape
    // recognition — just a sanity check that something was drawn in the guide area.
    if (checkDrawing && rect) {
      const pts = strokes.flatMap(s => s.points)
      if (pts.length < 8) { setDrawFeedback('✍️ draw the character in the box'); shake(boardRef.current); return }
      const xs = pts.map(p => p.x), ys = pts.map(p => p.y)
      const bw = Math.max(...xs) - Math.min(...xs), bh = Math.max(...ys) - Math.min(...ys)
      if (bw / rect.width < 0.2 && bh / rect.height < 0.2) { setDrawFeedback('a bit small — fill more of the box'); shake(boardRef.current); return }
      setDrawFeedback('')
    }

    const done = new Set([...doneChars, charIdx])
    setDoneChars(done)
    if (done.size >= chars.length) {
      if (rect) celebrate(rect.left + rect.width / 2, rect.top + rect.height / 2, { count: 40, spread: 220 })
      setWordComplete(true)
      speakText(item.text)
      advanceTimerRef.current = setTimeout(nextWord, 2200)
    } else {
      if (rect) celebrate(rect.left + rect.width / 2, rect.top + rect.height / 3, { count: 14, spread: 90 })
      // Move to the next character that isn't done yet.
      let next = charIdx
      for (let i = 1; i <= chars.length; i++) {
        const candidate = (charIdx + i) % chars.length
        if (!done.has(candidate)) { next = candidate; break }
      }
      setCharIdx(next)
    }
  }

  const goChar = (dir) => {
    if (chars.length === 0) return
    setCharIdx(prev => (prev + dir + chars.length) % chars.length)
  }

  if (items.length === 0) {
    return (
      <div className={styles.view}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.empty}>No cards with text to practice writing.</div>
      </div>
    )
  }

  const romanWord = romanizationOf(item.text, item.lang)
  const romanChar = romanizationOf(currentChar, item.lang)

  return (
    <div className={styles.view}>
      <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>

      <div className={styles.header}>
        <div className={styles.modeLabel}>Handwriting · {languages[item.lang] || item.lang}</div>

        <div className={styles.wordRow}>
          <div className={styles.charChips}>
            {chars.map((ch, i) => (
              <button
                key={i}
                className={[
                  styles.charChip,
                  i === charIdx && !wordComplete ? styles.charChipCurrent : '',
                  doneChars.has(i) ? styles.charChipDone : '',
                ].join(' ')}
                onClick={() => { setCharIdx(i); setWordComplete(false) }}
              >
                {ch}
              </button>
            ))}
          </div>
          <button className={styles.iconBtn} onClick={() => speakText(item.text)} title="Hear the word">
            <VolumeUpIcon fontSize="small" />
          </button>
        </div>

        <div className={styles.meta}>
          {romanWord && <span className={styles.roman}>{romanWord}</span>}
          {item.translation && <span className={styles.translation}>{item.translation}</span>}
        </div>
      </div>

      <div className={styles.boardWrap}>
        <div
          ref={boardRef}
          className={styles.board}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <svg className={styles.gridLines}>
            <line x1="50%" y1="0" x2="50%" y2="100%" />
            <line x1="0" y1="50%" x2="100%" y2="50%" />
            <line x1="0" y1="0" x2="100%" y2="100%" />
            <line x1="100%" y1="0" x2="0" y2="100%" />
          </svg>

          {showGuide && !wordComplete && (
            <div className={styles.ghost} ref={ghostRef}>{currentChar}</div>
          )}

          <svg className={styles.ink}>
            {strokes.map(s => <StrokeRender key={s.id} stroke={s} />)}
            {currentStroke && <StrokeRender stroke={currentStroke} />}
          </svg>

          {wordComplete && (
            <div className={styles.completeOverlay}>
              <div className={styles.completeWord}>{item.text}</div>
              {romanWord && <div className={styles.completeRoman}>{romanWord}</div>}
              <div className={styles.completeSub}>beautiful ✍️</div>
            </div>
          )}
        </div>

        {romanChar && !wordComplete && (
          <div className={styles.charRoman}>{romanChar}</div>
        )}
        {drawFeedback && (
          <div style={{ color: '#fbbf24', fontSize: '0.85rem', fontFamily: "'DM Mono', monospace", marginTop: '0.4rem', textAlign: 'center' }}>{drawFeedback}</div>
        )}
      </div>

      <div className={styles.toolbar}>
        <button className={styles.toolBtn} onClick={() => goChar(-1)} title="Previous character">
          <NavigateBeforeIcon fontSize="small" />
        </button>
        <button className={styles.toolBtn} onClick={undo} disabled={strokes.length === 0} title="Undo stroke">
          <UndoIcon fontSize="small" />
        </button>
        <button className={styles.toolBtn} onClick={clear} disabled={strokes.length === 0} title="Clear board">
          <DeleteOutlineIcon fontSize="small" />
        </button>
        <button className={styles.toolBtn} onClick={() => setShowGuide(g => !g)} title={showGuide ? 'Hide guide (test yourself)' : 'Show guide'}>
          {showGuide ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
        </button>
        <button className={styles.toolBtn} onClick={() => speakText(currentChar)} title="Hear this character">
          <VolumeUpIcon fontSize="small" />
        </button>
        <button className={`${styles.toolBtn} ${styles.toolBtnPrimary}`} onClick={markCharDone} title="Done — next character">
          <CheckIcon fontSize="small" />
        </button>
        <button className={styles.toolBtn} onClick={() => goChar(1)} title="Next character">
          <NavigateNextIcon fontSize="small" />
        </button>
        <div className={styles.toolDivider} />
        <button className={styles.wordBtn} onClick={nextWord}>
          Next word <NavigateNextIcon fontSize="small" />
        </button>
      </div>
    </div>
  )
}
